import { useParams } from 'react-router-dom'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'
import { PayNowFlow } from '@/components/member/pay-now-flow'
import { useQuickPay, useQuickPayPeek } from '@/hooks/use-payments'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/utils'

/** Public, no-login page for the invoice-summary email's "Pay now" link. GET (the peek query
 * below) has no side effects on purpose — email/link scanners pre-fetch every URL in an email,
 * and only an explicit click on PayNowFlow's button ever fires the actual STK push (a POST). */
export function QuickPayPage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useQuickPayPeek(token ?? null)
  const pay = useQuickPay()

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />
      <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">Pay your bill</h2>

      {isLoading && <p className="text-center font-sans text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && (error || !data) && (
        <p className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 text-center font-sans text-sm text-destructive">
          {getApiErrorMessage(error, 'This payment link has expired or is no longer valid.')}
        </p>
      )}

      {!isLoading && data && (
        <div className="space-y-5 text-center">
          <p className="font-sans text-sm text-muted-foreground">
            Hello {data.data.memberName}, you owe{' '}
            <strong className="text-ink">{formatKES(data.data.amount)}</strong>.
          </p>
          <div className="flex justify-center">
            <PayNowFlow
              amount={data.data.amount}
              defaultPhone={data.data.phone}
              paybill={data.data.paybill}
              accountReference={data.data.accountReference}
              isPaying={pay.isPending}
              pollStatus={false}
              onPay={async (phone) => {
                const { data: result } = await pay.mutateAsync({ token: token!, phone })
                return result
              }}
            />
          </div>
        </div>
      )}
    </AuthCard>
  )
}
